import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { api } from "../lib/api";

const STYLES = ["Cartoony / Playful", "Professional / Corporate", "Minimal / Clean", "Bold / Edgy", "Luxury / Premium", "Friendly / Casual"];
const TONES = ["Professional", "Fun", "Bold", "Friendly", "Authoritative", "Witty"];
const CONTENT_GOALS = [
  { id: "organic", label: "Organic social media", sub: "Posts to grow followers, build trust, stay top of mind." },
  { id: "paid", label: "Paid ads", sub: "Creatives built to convert — Meta, TikTok, YouTube ads." },
  { id: "both", label: "Both", sub: "Mix of organic posts + ad creatives in the same delivery." },
];
const ORGANIC_STYLES = [
  "Behind-the-scenes / day-in-the-life",
  "Before & after / transformation",
  "Tips & educational",
  "Showcase / portfolio shots",
  "Trending / meme-driven",
  "Customer testimonials & social proof",
];
const ADS_STYLES = [
  "Hook-driven (stop-the-scroll opener)",
  "Problem / solution",
  "Before & after transformation",
  "Testimonial / UGC-style",
  "Offer-led (price / deal forward)",
  "Founder talking-head / authority",
];

const initialForm = {
  business_name: "",
  industry: "",
  brand_colors: "",
  target_audience: "",
  tone_of_voice: "Professional",
  content_style: "Professional / Corporate",
  content_goal: "organic",
  organic_styles: [],
  ads_styles: [],
  products_services: "",
  social_or_website: "",
  customer_email: "",
  additional_notes: "",
};

export default function IntakePage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id") || params.get("ref") || params.get("orderId");
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking, paid, expired, unverified
  const [form, setForm] = useState(initialForm);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [pkgInfo, setPkgInfo] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("unverified");
      return;
    }
    let attempts = 0;
    const max = 8;
    const poll = async () => {
      try {
        const res = await api.get(`/checkout/status/${sessionId}`);
        setPkgInfo(res.data);
        if (res.data.payment_status === "paid") {
          setStatus("paid");
          // Facebook Pixel: Purchase event (deduplicated with backend CAPI via shared event_id)
          if (typeof window !== "undefined" && window.fbq && !window.__fbqPurchaseFired) {
            window.__fbqPurchaseFired = true;
            const eventId = `pu_${sessionId}`;
            window.fbq(
              "track",
              "Purchase",
              {
                value: res.data.amount_total || 0,
                currency: (res.data.currency || "usd").toUpperCase(),
                content_ids: [res.data.package_id],
              },
              { eventID: eventId }
            );
          }
          return;
        }
        if (res.data.status === "expired") {
          setStatus("expired");
          return;
        }
        attempts += 1;
        if (attempts < max) {
          setTimeout(poll, 2200);
        } else {
          setStatus("unverified");
        }
      } catch {
        attempts += 1;
        if (attempts < max) setTimeout(poll, 2200);
        else setStatus("unverified");
      }
    };
    poll();
  }, [sessionId]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleInArray = (key, value) =>
    setForm((f) => {
      const arr = f[key] || [];
      return {
        ...f,
        [key]: arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value],
      };
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/intake", { ...form, session_id: sessionId });
      navigate("/success");
    } catch (err) {
      alert("Could not submit your form. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (status === "checking") {
    return (
      <div className="min-h-screen bg-[#080C14] flex items-center justify-center" data-testid="intake-checking">
        <div className="text-center">
          <Loader2 className="animate-spin text-[#3B9EFF] mx-auto" size={36} />
          <p className="mt-4 text-[#A0AABF]">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-grain min-h-screen" data-testid="intake-page">
      <Header />
      <main className="py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          {status === "paid" && (
            <div className="snap-card border-[#3B9EFF]/50 mb-8" data-testid="intake-payment-confirmed">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[#3B9EFF]/15 border border-[#3B9EFF]/50 flex items-center justify-center text-[#3B9EFF] shrink-0">
                  <CheckCircle2 size={20} />
                </div>
                <div>
                  <p className="font-accent text-xs text-[#3B9EFF]">PAYMENT CONFIRMED</p>
                  <h2 className="font-display text-2xl font-bold text-white mt-1">You're in. Let's gather the details.</h2>
                  <p className="text-[#A0AABF] mt-2 text-sm">
                    Fill this 2-minute form so we can craft content tailored to your brand. Your delivery clock starts now.
                  </p>
                </div>
              </div>
            </div>
          )}

          {(status === "unverified" || status === "expired") && (
            <div className="snap-card mb-8 border-orange-500/40" data-testid="intake-unverified">
              <p className="font-accent text-xs text-orange-400">⚠ NO PAYMENT DETECTED</p>
              <h2 className="font-display text-2xl font-bold text-white mt-1">Continue with intake or complete checkout</h2>
              <p className="text-[#A0AABF] mt-2 text-sm">
                We couldn't verify a paid session. You can still submit the form (we'll follow up to confirm payment), or grab a pack below.
              </p>
              <div className="mt-4 flex gap-3 flex-wrap">
                <Link to="/offer/pack-99" className="btn-primary text-sm py-2.5 px-5" data-testid="intake-buy-99">Buy $99.99 Pack</Link>
                <Link to="/offer/monthly-175" className="btn-secondary text-sm py-2.5 px-5" data-testid="intake-buy-175">$175/mo Plan</Link>
              </div>
            </div>
          )}

          <div className="snap-card relative">
            <div className="corner-bracket tl" />
            <div className="corner-bracket tr" />
            <div className="flex items-center justify-between mb-6">
              <h1 className="font-display text-3xl font-bold text-white">Your Content Brief</h1>
              <span className="font-accent text-xs text-[#3B9EFF]">STEP {step} / 3</span>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5" data-testid="intake-form">
              {step === 1 && (
                <div className="space-y-5" data-testid="intake-step-1">
                  <Field label="Your email (for delivery)">
                    <input required type="email" className="snap-input" value={form.customer_email} onChange={(e) => update("customer_email", e.target.value)} data-testid="intake-email" />
                  </Field>
                  <Field label="Business name">
                    <input required className="snap-input" value={form.business_name} onChange={(e) => update("business_name", e.target.value)} data-testid="intake-business-name" />
                  </Field>
                  <Field label="Industry / niche">
                    <input required className="snap-input" placeholder="e.g. roofing, salon, fitness coach" value={form.industry} onChange={(e) => update("industry", e.target.value)} data-testid="intake-industry" />
                  </Field>
                  <Field label="Brand colors (hex or names)">
                    <input className="snap-input" placeholder="#FF5733, navy, gold" value={form.brand_colors} onChange={(e) => update("brand_colors", e.target.value)} data-testid="intake-colors" />
                  </Field>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-5" data-testid="intake-step-2">
                  <Field label="Who's your target customer?">
                    <textarea required rows={3} className="snap-input resize-none" placeholder="e.g. homeowners 35-65 in suburban Texas" value={form.target_audience} onChange={(e) => update("target_audience", e.target.value)} data-testid="intake-audience" />
                  </Field>
                  <Field label="Tone of voice">
                    <select className="snap-input" value={form.tone_of_voice} onChange={(e) => update("tone_of_voice", e.target.value)} data-testid="intake-tone">
                      {TONES.map((t) => <option key={t} value={t} className="bg-[#0B111D]">{t}</option>)}
                    </select>
                  </Field>
                  <Field label="Content style">
                    <div className="grid grid-cols-2 gap-2">
                      {STYLES.map((s) => (
                        <button
                          type="button"
                          key={s}
                          onClick={() => update("content_style", s)}
                          className={`text-left px-3 py-2.5 rounded-md border text-sm transition-all ${form.content_style === s ? "border-[#3B9EFF] bg-[#3B9EFF]/10 text-white" : "border-[#3B9EFF]/20 text-[#A0AABF] hover:text-white"}`}
                          data-testid={`intake-style-${s.split(" ")[0].toLowerCase()}`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </Field>

                  <Field label="What's this content for?">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="intake-content-goal">
                      {CONTENT_GOALS.map((g) => {
                        const active = form.content_goal === g.id;
                        return (
                          <button
                            type="button"
                            key={g.id}
                            onClick={() => update("content_goal", g.id)}
                            className={`text-left px-3 py-3 rounded-md border transition-all ${active ? "border-[#3B9EFF] bg-[#3B9EFF]/10" : "border-[#3B9EFF]/20 hover:border-[#3B9EFF]/40"}`}
                            data-testid={`intake-goal-${g.id}`}
                          >
                            <span className={`block text-sm font-semibold ${active ? "text-white" : "text-white/85"}`}>
                              {g.label}
                            </span>
                            <span className="block text-[11px] text-[#A0AABF] mt-1 leading-snug">
                              {g.sub}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </Field>

                  {(form.content_goal === "organic" || form.content_goal === "both") && (
                    <Field label="Organic post styles you want (pick any that fit)">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="intake-organic-styles">
                        {ORGANIC_STYLES.map((s) => {
                          const active = form.organic_styles.includes(s);
                          return (
                            <button
                              type="button"
                              key={s}
                              onClick={() => toggleInArray("organic_styles", s)}
                              className={`text-left px-3 py-2.5 rounded-md border text-sm transition-all ${active ? "border-[#3B9EFF] bg-[#3B9EFF]/10 text-white" : "border-[#3B9EFF]/20 text-[#A0AABF] hover:text-white"}`}
                              data-testid={`intake-organic-${s.split(" ")[0].toLowerCase()}`}
                            >
                              {active ? "✓ " : ""}{s}
                            </button>
                          );
                        })}
                      </div>
                    </Field>
                  )}

                  {(form.content_goal === "paid" || form.content_goal === "both") && (
                    <Field label="Ad creative angles you want (pick any that fit)">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="intake-ads-styles">
                        {ADS_STYLES.map((s) => {
                          const active = form.ads_styles.includes(s);
                          return (
                            <button
                              type="button"
                              key={s}
                              onClick={() => toggleInArray("ads_styles", s)}
                              className={`text-left px-3 py-2.5 rounded-md border text-sm transition-all ${active ? "border-[#3B9EFF] bg-[#3B9EFF]/10 text-white" : "border-[#3B9EFF]/20 text-[#A0AABF] hover:text-white"}`}
                              data-testid={`intake-ad-${s.split(" ")[0].toLowerCase()}`}
                            >
                              {active ? "✓ " : ""}{s}
                            </button>
                          );
                        })}
                      </div>
                    </Field>
                  )}
                </div>
              )}

              {step === 3 && (
                <div className="space-y-5" data-testid="intake-step-3">
                  <Field label="Products or services to feature">
                    <textarea required rows={3} className="snap-input resize-none" placeholder="List the top 2-3 things you want to push" value={form.products_services} onChange={(e) => update("products_services", e.target.value)} data-testid="intake-products" />
                  </Field>
                  <Field label="Instagram or website link (for reference)">
                    <input className="snap-input" placeholder="instagram.com/yourbiz or yourbusiness.com" value={form.social_or_website} onChange={(e) => update("social_or_website", e.target.value)} data-testid="intake-social" />
                  </Field>
                  <Field label="Anything else we should know?">
                    <textarea rows={3} className="snap-input resize-none" placeholder="Optional notes" value={form.additional_notes} onChange={(e) => update("additional_notes", e.target.value)} data-testid="intake-notes" />
                  </Field>
                </div>
              )}

              <div className="flex items-center justify-between pt-2">
                <button
                  type="button"
                  onClick={() => setStep((s) => Math.max(1, s - 1))}
                  className="text-[#A0AABF] hover:text-white text-sm disabled:opacity-30"
                  disabled={step === 1}
                  data-testid="intake-back"
                >
                  ← Back
                </button>
                {step < 3 ? (
                  <button type="button" onClick={() => setStep((s) => s + 1)} className="btn-primary" data-testid="intake-next">
                    Next <ArrowRight size={18} />
                  </button>
                ) : (
                  <button type="submit" disabled={submitting} className="btn-primary" data-testid="intake-submit">
                    {submitting ? "Sending..." : "Submit Brief"} <ArrowRight size={18} />
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="font-accent text-xs text-[#A0AABF]">{label}</label>
    <div className="mt-2">{children}</div>
  </div>
);
